import { z } from "zod";

import { API_DOMAIN_REGEX } from "@shared/constants";
import {
    Address,
    EVMChainConfig,
    SolChainConfig,
    SolTransactionReceipt,
    SolTransactionRequest,
    TransactionReceipt,
    TransactionRequest,
} from "@shared/types";
import { Flag, IRequestForInputs, IRequestForOutputs, RampName, type ITransferInInstruction } from "@halliday-lib/api";
import { AddressFamilyMap, IAsset, Ip } from "@halliday-lib/schema";

// TypedData is a simple string in the SDK context (typed data payload for signing).
// HallidayLib's EIP712TypedData is a structured object — not the same concept.
type TypedData = string;

const WindowType = z.enum(["EMBED", "MODAL"]);
export type WindowType = z.infer<typeof WindowType>;

export const BorderStyle = z.enum(["SQUARE", "DEFAULT"]);
export type BorderStyle = z.infer<typeof BorderStyle>;

export const BackgroundStyle = z.enum(["BLUR", "OFF"]);
export type BackgroundStyle = z.infer<typeof BackgroundStyle>;

const CssColor = z.string().refine(
    (val) => {
        // Regular expression for matching CSS color formats (hex, rgb, rgba)
        const cssColorRegex =
            /^(#[0-9A-Fa-f]{3,8}|rgb\(\d{1,3},\s*\d{1,3},\s*\d{1,3}\)|rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3}(\s*,\s*(0(\.\d+)?|1(\.0+)?))\))$/;
        return cssColorRegex.test(val);
    },
    {
        message: "Invalid CSS color value",
    },
);

export const CssFontSize = z.string().refine((val) => /^\d*\.?\d+px$/.test(val), {
    message: "Invalid CSS font size value. Expected a number followed by px (e.g. '16px').",
});
export type CssFontSize = z.infer<typeof CssFontSize>;

export const CssBorderRadius = z.string().refine((val) => /^\d*\.?\d+(px|rem)$/.test(val), {
    message: "Invalid CSS border radius value. Expected a number followed by px or rem (e.g. '8px', '0.5rem').",
});
export type CssBorderRadius = z.infer<typeof CssBorderRadius>;

export const CustomStyles = z.object({
    primaryColor: CssColor.optional(),
    backgroundColor: CssColor.optional(),
    borderColor: CssColor.optional(),
    textColor: CssColor.optional(),
    textSecondaryColor: CssColor.optional(),
    accentColor: CssColor.optional(),
    borderStyle: BorderStyle.optional(),
    successColor: CssColor.optional(),
    alertColor: CssColor.optional(),
    zIndex: z.number().int().optional(),

    // Internal, not documented
    componentShadow: z.string().optional(),
    backgroundStyle: BackgroundStyle.optional(),
});
export type CustomStyles = z.infer<typeof CustomStyles>;

export const OrderStatus = z.any(); // TODO: Define the type for the order status
export type OrderStatus = z.infer<typeof OrderStatus>;

// A single order needing the user's attention, forwarded from the widget. A REAL zod schema (NOT
// z.any() — deliberately avoids the OrderStatus debt). `issue` is an OPEN string carrying the widget's
// per-order typeKey verbatim, so unknown future classifications still validate and reach the snapshot.
export const OrderNotification = z.object({
    paymentId: z.string(),
    issue: z.string(), // open vocabulary; current values: ORDER_TAINTED | ORDER_PARTIALLY_FUNDED
    // | ORDER_FAILED | ORDER_UNEXPECTED_BALANCE | ORDER_FUNDED_UNEXPECTED_BALANCE
    message: z.string(), // human-friendly, rendered by the widget
});
export type OrderNotification = z.infer<typeof OrderNotification>;

// Full-set payload for EVENT_ORDER_NOTIFICATIONS — the widget re-sends the WHOLE set on every change
// (including shrinks as orders resolve). Used with safeParse at the wire boundary.
export const OrderNotifications = z.array(OrderNotification);
export type OrderNotifications = z.infer<typeof OrderNotifications>;

// FontName is a string that represents the name of the font to use. (default: "haffer")
export const FontName = z.string();
export type FontName = z.infer<typeof FontName>;

export const HeaderTitle = z.string().optional();
export type HeaderTitle = z.infer<typeof HeaderTitle>;

// Function types
export type SignMessage = (input: { message: string; ownerAddress?: Address }) => Promise<string>;
export type SignTypedData = (input: { typedData: TypedData; ownerAddress?: Address; chainId?: number }) => Promise<string>;

// Dynamic's own chain codes — identical to AddressFamilyMap's keys (identity mapping).
export const WalletAuthChain = z.enum(["EVM", "SOL", "SUI"]);
export type WalletAuthChain = z.infer<typeof WalletAuthChain>;

export const WalletVm = z.enum(["EVM", "SOL"]);
export type WalletVm = z.infer<typeof WalletVm>;

/** Compile-time proof that every WalletVm member is an AddressFamilyMap key, so the tag's values
 *  can never drift from the address family they name. Errors at declaration if that stops holding. */
type Assert<T extends true> = T;
export type WalletVmIsAddressFamily = Assert<WalletVm extends keyof AddressFamilyMap ? true : false>;

// Host signer for auth login. Distinct from EVM `SignMessage`: carries the chain and must
// handle non-EVM signing. CONTRACT: sign `message` verbatim — any reformat breaks Dynamic verification.
export type SignAuthMessage = (input: {
    message: string; // the exact SDK-produced sign-in message; sign as-is
    address: Address; // the wallet expected to sign
    walletType: WalletAuthChain;
}) => Promise<string>; // returns the signature

export type SendTransaction = (
    transaction: TransactionRequest,
    chainConfig: EVMChainConfig,
) => Promise<TransactionReceipt>;

export type WalletActionsType = {
    getAddress: () => Promise<Address>;
    signMessage: SignMessage;
    sendTransaction: SendTransaction;
    signTypedData: SignTypedData;
    walletType?: WalletVm;
};

export type StatusCallback = (input: { type: string; payload: OrderStatus }) => void;

// Internal bridge callback — mirrors StatusCallback. NOT public host config; the constructor injects it.
export type NotificationsCallback = (notifications: OrderNotification[]) => void;

export const PaymentFlowType = z.enum([
    "wallet",
    "cash",
    "exchange",
    "deposit",
    "recover",
    "withdraw",
    "offramp",
    // Workflow label for orders resuming an unused RDW balance; not a host-selectable flow.
    "rdw-resume",
]);
export type PaymentFlowType = z.infer<typeof PaymentFlowType>;

export const PaymentDirection = z.enum(["inbound", "outbound"]);
export type PaymentDirection = z.infer<typeof PaymentDirection>;

/**
 * Host-provided checkout session — the SDK is the source of truth for this contract (halliday-apps
 * will later import it in place of its local copy). Mirrors halliday-apps `types/paymentFlow.ts`.
 * On OUTPUT (z.infer) `direction` defaults to 'inbound' and `locked` to false (both required); on
 * INPUT (z.input) both are optional — hence the open* method args use `z.input<...>`.
 */
export const PaymentSession = z.object({
    direction: PaymentDirection.optional().default("inbound"),
    input: z.object({ asset: z.string(), amount: z.string().optional() }).optional(),
    output: z.string().optional(),
    inputFiatValue: z.object({ currency: z.string(), amount: z.string() }).optional(),
    fundingAddress: z.string().optional(),
    destination: z.string().optional(),
    locked: z.boolean().optional().default(false),
});
export type PaymentSession = z.infer<typeof PaymentSession>;

const Role = z.object({
    getAddress: z.any().refine((val) => typeof val === "function"),
    signMessage: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    sendTransaction: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    signTypedData: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    walletName: z.string().optional(),
    walletType: z
        .string()
        .toUpperCase()
        .pipe(WalletVm)
        .optional()
        .catch((ctx) => {
            console.warn(`[Halliday] Unrecognized walletType ${JSON.stringify(ctx.value)}.`);
            return undefined;
        }),
});

export type Role = z.infer<typeof Role>;

const isFn = (val: unknown): boolean => typeof val === "function";

const CustomRole = z.object({
    walletName: z.string().optional(),
    getBalances: z.any().refine(isFn, { message: "getBalances must be a function" }),
    sendFunds: z.any().refine(isFn, { message: "sendFunds must be a function" }),
    getFee: z.any().refine(isFn, { message: "getFee must be a function" }).optional(),
});

/**
 * A funder is EITHER a chain wallet (`getAddress`/`sendTransaction`) OR a host-managed custom funder
 * (`getBalances`/`sendFunds`) — never both.
 */
const FunderRoleSchema = z
    .custom<Record<string, unknown>>((val) => typeof val === "object" && val !== null, {
        message: "A funder must be an object",
    })
    .superRefine((bag, ctx) => {
        if (!isFn(bag.getBalances) && !isFn(bag.sendFunds)) return;
        if (isFn(bag.getAddress) || isFn(bag.sendTransaction)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    "A funder must be either a wallet role (getAddress/sendTransaction) or a custom role (getBalances/sendFunds), not both.",
            });
        }
        if (bag.walletType !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["walletType"],
                message: "A custom funder is VM-agnostic and must not carry walletType.",
            });
        }
    })
    .pipe(z.union([CustomRole, Role]));

export const WalletMeta = z.object({
    address: Address,
    walletName: z.string().optional(),
    flowType: PaymentFlowType.optional(),
    hasFee: z.boolean().optional(),
    // The funder's VM, carried to the widget. `.catch(undefined)` keeps a FUTURE SDK that emits a
    // third VM from hard-failing `deserializeQueryParams` in an already-deployed widget (that parse
    // throws and `parseAppConfigs` then returns null, i.e. a dead widget).
    walletType: WalletVm.optional().catch(undefined),
});
export type WalletMeta = z.infer<typeof WalletMeta>;

// ============================================
// Owner auth-modes union (zod)
// ============================================
// The public `owner` param is a discriminated union on `type` (absent ⇒ 'direct'): today's
// host-wallet owner (`direct`), a SIWE-style host-signed login (`wallet-auth`), or an email-OTP
// login (`otp-auth`). This zod schema validates at runtime; the hand-written `Owner` type (declared
// after `OwnerRole` below) carries the typed callbacks. They are intentionally NOT paired via z.infer
// — mirroring how `OwnerRole` is hand-written while the zod side uses `z.any()` for functions.
// NOTE: defined here (before PaymentsWidgetSDKParamsBase) because that schema references OwnerSchema.

// today's host-wallet owner; `type` optional for back-compat (normalized to 'direct' in preprocess).
const DirectOwner = Role.extend({ type: z.literal("direct") });
// SIWE-style login: the host signs the widget's sign-in message out-of-band via signAuthMessage.
// Kept a PURE object (no per-arm effects) so z.discriminatedUnion can statically extract `type`;
// `address` is family-checked against `walletType` in the outer superRefine.
const WalletAuthOwner = z.object({
    type: z.literal("wallet-auth"),
    walletType: WalletAuthChain,
    address: Address,
    signAuthMessage: z.any().refine((val) => typeof val === "function"),
});
// email-OTP login: the widget auto-sends the code to this host-supplied email.
const OtpAuthOwner = z.object({ type: z.literal("otp-auth"), address: z.email() });

// Absent `type` → 'direct', then discriminate, then a cross-field per-walletType address check.
// The address check is a single superRefine on the OUTER union and is validate-only: EVMAddress/
// SuiAddress `.transform()` to lowercase, so we `safeParse` rather than pipe — the value passes
// through untransformed.
const OwnerSchema = z
    .preprocess(
        (val) => (val && typeof val === "object" && !("type" in val) ? { ...(val as object), type: "direct" } : val),
        z.discriminatedUnion("type", [DirectOwner, WalletAuthOwner, OtpAuthOwner]),
    )
    .superRefine((val, ctx) => {
        if (val.type === "wallet-auth") {
            const schema = AddressFamilyMap.shape[val.walletType]; // identity mapping; validate only
            if (!schema.safeParse(val.address).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["address"],
                    message: `Invalid ${val.walletType} address`,
                });
            }
        }
        // A direct owner's `sendTransaction` is EVM-only (`SendTransaction`), so EVM is the only VM it
        // can honestly claim — mirrors the `walletType?: "EVM"` narrowing on `OwnerRole`. Rejected
        // rather than silently corrected: a host passing "SOL" here believes it registered a Solana
        // funder, and quietly rewriting that to EVM is how a transfer ends up built for the wrong VM.
        // NOT defaulted — absence is handled by the shared `?? "EVM"` when threading to funderMeta, and
        // a `.default()` would stamp a VM onto signing-only owners that have none.
        if (val.type === "direct" && val.walletType !== undefined && val.walletType !== "EVM") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["walletType"],
                message: `A direct owner must be EVM (got "${val.walletType}"): its sendTransaction is EVM-only. Register a ${val.walletType} wallet via funder/funders instead.`,
            });
        }
    });

export const UserWalletFunderDisplay = z.enum(["SHOW", "HIDE"]);
export type UserWalletFunderDisplay = z.infer<typeof UserWalletFunderDisplay>;

// Fee-sponsorship grant minted on demand by the host — the camelCase shape the `generateFeeSponsorshipGrant`
// callback returns and the grant round-trip message carries. The wire/snake_case form
// (`{ grant_payload, grant_signature }`) lives on the HallidayLib quote types; the `*Payload` values are the
// same JSON string, so mapping across the two boundaries is a pure rename.
export const FeeSponsorshipGrant = z.object({
    grantPayload: z.string(),
    grantSignature: z.string(),
});
export type FeeSponsorshipGrant = z.infer<typeof FeeSponsorshipGrant>;

// Payments Widget params
const PaymentsWidgetSDKParamsBase = z.object({
    apiKey: z.string().nonempty(),

    // inputs filters
    outputs: IRequestForInputs.shape.outputs,
    // source/funding assets — promoted flat param (sourced from deposit.inputs; replaces dangerouslyOverrideInputs)
    inputs: IRequestForInputs.shape.inputs,
    sandbox: z.boolean().optional().default(false),

    // withdraw
    withdrawInputs: IRequestForOutputs.shape.inputs.optional(),

    // tags — unvalidated here; the widget validates against HallidayLib's `Tags` at quote time
    depositTags: z.array(z.string()).optional(),
    withdrawTags: z.array(z.string()).optional(),

    // fee sponsorship — flattened from `feeSponsorship.profileName`. A serializable business param, kept OUT
    // of the WithoutRolesAndFunctions omit list, so it flows to the widget query params as `profileName` AND
    // is live-pushed by `_pushParamUpdate` on a mid-session rename (never baked into the URL via SDKAdditions).
    profileName: z.string().nonempty().optional(),

    // widget display
    customStyles: CustomStyles.optional(),
    targetElementId: z.string().optional(),
    fontName: FontName.optional(),
    headerTitle: HeaderTitle.optional(),
    defaultLandingTab: z.enum(["crypto", "cash"]).optional(),

    // functions
    onStatus: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    onNotifications: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    onConnectWallet: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),
    generateFeeSponsorshipGrant: z
        .any()
        .refine((val) => typeof val === "function")
        .optional(),

    // roles
    funder: FunderRoleSchema.optional(),
    funders: z.array(FunderRoleSchema).optional(),
    owner: OwnerSchema.optional(),
    destinationAddress: Address.optional(),

    // Withdrawals
    withdrawFunder: FunderRoleSchema.optional(),
    withdrawDestinationAddress: Address.optional(),

    // Preload callbacks (used by both initializeClient and openHallidayPayments)
    /** Callback when widget is preloaded and ready */
    onReady: z
        .any()
        .refine((val) => typeof val === "function" || val === undefined)
        .optional(),
    /** Callback if preloading fails */
    onError: z
        .any()
        .refine((val) => typeof val === "function" || val === undefined)
        .optional(),
});

export const PaymentsWidgetSDKParams = PaymentsWidgetSDKParamsBase;

const PaymentsWidgetSDKParamsWithoutRolesAndFunctions = PaymentsWidgetSDKParamsBase.omit({
    funder: true,
    funders: true,
    owner: true,
    withdrawFunder: true,
    onStatus: true,
    onNotifications: true,
    onReady: true,
    onError: true,
    onConnectWallet: true,
    generateFeeSponsorshipGrant: true,
});

// SOL sibling of `SendTransaction` (which stays EVM). Kept separate rather than widening
// SendTransaction so the EVM host contract is untouched.
export type SolSendTransaction = (
    transaction: SolTransactionRequest,
    chainConfig: SolChainConfig,
) => Promise<SolTransactionReceipt>;

/** Host-supplied balances */
export type GetBalances = () => Promise<[assetIdentifier: string, balance: string][]>;

/** Host-managed fund movement */
export type SendFunds = (instruction: ITransferInInstruction) => Promise<void>;

export type GetFee = (assetIdentifier: string, amount: string) => Promise<string> | string;

/**
 * The type-level half of "a funder is a chain wallet OR a host-managed custom funder, never both".
 */
type NoCustomFunderMembers = { getBalances?: never; sendFunds?: never };
type NoWalletFunderMembers = { getAddress?: never; sendTransaction?: never; walletType?: never };

/**
 * EVM funder. NARROWS the inherited `WalletActionsType["walletType"]` to the literal, which is what
 * keeps `FunderRole` a usable discriminated union — with the base's `WalletVm` left unnarrowed,
 * `Extract<FunderRole, { walletType: "SOL" }>` resolves to `never`. Still OPTIONAL so existing
 * untagged host code typechecks.
 */
export type EvmFunderRole = Omit<WalletActionsType, "signTypedData" | "signMessage"> & {
    walletType?: "EVM";
    walletName?: string;
} & NoCustomFunderMembers;

/** SOL funder. The tag is REQUIRED — it is what discriminates the union. */
export type SolFunderRole = {
    walletType: "SOL";
    getAddress: () => Promise<Address>;
    sendTransaction: SolSendTransaction;
    walletName?: string;
} & NoCustomFunderMembers;

/**
 * Host-managed ("DIY") funder.
 */
export type CustomFunderRole = {
    walletName?: string;
    getBalances: GetBalances;
    sendFunds: SendFunds;
    getFee?: GetFee;
} & NoWalletFunderMembers;

export type FunderRole = EvmFunderRole | SolFunderRole | CustomFunderRole;

export type EvmWalletRole = WalletActionsType & { walletType: "EVM" };

// The user/owner wallet; may also fund (optional sendTransaction). Exact shape `userWallet` carried.
// This is the `direct` arm building block of the `Owner` union below.
export type OwnerRole = Omit<WalletActionsType, "sendTransaction"> & {
    sendTransaction?: SendTransaction;
    walletName?: string;
    walletType?: "EVM";
};

// Public owner union (hand-written for the typed callbacks; `OwnerSchema` above validates at runtime).
// NOTE: `address` is a wallet address for direct/wallet-auth but an EMAIL for otp-auth — consumers must
// gate on `type` before reading it.
export type Owner =
    | ({ type?: "direct" } & OwnerRole) // today's host-wallet owner; `type` optional for compat
    | { type: "wallet-auth"; walletType: WalletAuthChain; address: Address; signAuthMessage: SignAuthMessage }
    | { type: "otp-auth"; address: string /* email */ };

export type PaymentsWidgetSDKParams = z.input<typeof PaymentsWidgetSDKParamsWithoutRolesAndFunctions> & {
    // funder only needs getAddress + sendTransaction
    funder?: FunderRole;
    // multi-funder support
    funders?: FunderRole[];
    withdrawFunder?: FunderRole;
    // owner is the user wallet (direct arm), OR a wallet-auth / otp-auth login mode — see `Owner`.
    owner?: Owner;
    onStatus?: StatusCallback;
    onNotifications?: NotificationsCallback;
    onReady?: () => void;
    onError?: (error: Error) => void;
    onConnectWallet?: () => void;
    generateFeeSponsorshipGrant?: (payload: string) => Promise<string>;
};

// Accepted overrides
const apiBaseUrl = z.string().regex(API_DOMAIN_REGEX, "Invalid API baseUrl");
// Make it a record of strings to booleans for flexibility
const featureFlags = z.record(z.string(), z.boolean());
/** @internal — internal/testing feature flags; replaced wholesale on update. */
export type FeatureFlags = z.infer<typeof featureFlags>;

export const SDKOverrides = z.object({
    dangerouslyOverrideHallidayDomainName: z.url().optional(),
    dangerouslyOverrideApiBaseUrl: apiBaseUrl.optional(),
    dangerouslyOverrideIPAddress: Ip.optional(),
    dangerouslyOverrideFeatureFlags: featureFlags.optional(),
    dangerouslyOverrideOnramps: IRequestForInputs.shape.onramps,
    dangerouslyOverrideOfframps: IRequestForInputs.shape.offramps,
    dangerouslyOverrideFlags: IRequestForInputs.shape.flags,
    dangerouslyOverrideHops: z.array(z.string()).optional(),
    // Demo mode: presence enables demo, value picks the scenario.
    dangerouslyOverrideDemoScenario: z.string().optional(),
});

export type SDKOverrides = z.infer<typeof SDKOverrides>;

export const AppMode = z.enum(["FULL", "MODAL", "OVERLAY", "EMBED"]);
export type AppMode = z.infer<typeof AppMode>;

// Owner's serializable subset that travels to the widget via `ownerMeta` (owner functions, incl.
// `signAuthMessage`, never serialize). `type` absent ⇒ legacy direct owner (byte-for-byte compatible);
// `walletType` is set only for wallet-auth. NOTE: `address` is a wallet address for direct/wallet-auth
// but an EMAIL for otp-auth — widget consumers must gate on `type`.
export const OwnerMeta = z.object({
    address: Address,
    walletName: z.string().optional(),
    type: z.enum(["direct", "wallet-auth", "otp-auth"]).optional(),
    walletType: WalletAuthChain.optional().catch(undefined),
});
export type OwnerMeta = z.infer<typeof OwnerMeta>;

// Additional data computed by the SDK
export const SDKAdditions = z.object({
    appMode: AppMode.optional(),
    apiBaseUrl: apiBaseUrl.optional(),
    targetView: z.string().optional(),
    show: z.boolean().optional(),
    hasOwner: z.boolean(),
    hasTxHandler: z.boolean(),
    hasConnect: z.boolean().optional().default(false),
    hasFeeSponsorship: z.boolean().optional().default(false),
    hostOrigin: z.url().nullable(),
    hostUrl: z.url().nullable().optional(),
    ipAddress: Ip.optional(),
    featureFlags: featureFlags.optional(),
    ownerMeta: OwnerMeta.optional(),
    funderMeta: z.array(WalletMeta).optional(),
    onramps: IRequestForInputs.shape.onramps,
    offramps: IRequestForInputs.shape.offramps,
    flags: IRequestForInputs.shape.flags,
    hops: z.array(z.string()).optional(),
    // Remap of dangerouslyOverrideDemoScenario.
    demoScenario: z.string().optional(),
    // SDK package version forwarded to the widget for telemetry.
    sdkVersion: z.string().optional(),
});
export type SDKAdditions = z.infer<typeof SDKAdditions>;

// QueryParams passed to the widget.
// Uses looseObject so old SDK params (e.g. `ownerAddress`) pass through without being stripped.
export const PaymentsWidgetQueryParams = z.looseObject({
    // omit all functions and roles
    ...PaymentsWidgetSDKParamsWithoutRolesAndFunctions.shape,
    // add all additions
    ...SDKAdditions.shape,
    // Per-invocation checkout session — on the WIRE schema only (validated for free by
    // serializeQueryParams/deserializeQueryParams). Deliberately NOT on PaymentsWidgetSDKParams or
    // HallidayPaymentsConfig: a session is per-open intent, not durable instance config.
    session: PaymentSession.optional(),
});
export type PaymentsWidgetQueryParams = z.infer<typeof PaymentsWidgetQueryParams>;

// Message action types
export enum MessageType {
    ACTION_TRANSACTION = "ACTION_TRANSACTION",
    EVENT_ORDER_STATUS = "EVENT_ORDER_STATUS",
    EVENT_ORDER_NOTIFICATIONS = "EVENT_ORDER_NOTIFICATIONS",
    EVENT_WINDOW_CLOSE = "EVENT_WINDOW_CLOSE",
    ACTION_SIGN_MESSAGE = "ACTION_SIGN_MESSAGE",
    ACTION_PROVIDER_WIDGET = "ACTION_PROVIDER_WIDGET",
    ACTION_SIGN_TYPED_DATA = "ACTION_SIGN_TYPED_DATA",
    ACTION_SIGN_AUTH_MESSAGE = "ACTION_SIGN_AUTH_MESSAGE",
    ACTION_TRIGGER_CONNECT = "ACTION_TRIGGER_CONNECT",
    ACTION_GENERATE_FEE_SPONSORSHIP_GRANT = "ACTION_GENERATE_FEE_SPONSORSHIP_GRANT",
    EVENT_RESIZE = "EVENT_RESIZE",
    ACTION_GET_BALANCES = "ACTION_GET_BALANCES",
    ACTION_TRANSFER_FUNDS = "ACTION_TRANSFER_FUNDS",
    ACTION_GET_FEE = "ACTION_GET_FEE",
}

export type Message =
    | {
          type: MessageType.ACTION_TRANSACTION;
          // Both VMs ride this ONE channel: `transaction.from` is present on either shape, so the
          // host handler's address-keyed funder lookup routes SOL for free.
          payload: {
              transaction: TransactionRequest | SolTransactionRequest;
              messageId: string;
              chainConfig: EVMChainConfig | SolChainConfig;
          };
      }
    | { type: MessageType.EVENT_ORDER_STATUS; payload: OrderStatus }
    | { type: MessageType.EVENT_ORDER_NOTIFICATIONS; payload: OrderNotification[] }
    | { type: MessageType.EVENT_WINDOW_CLOSE; payload: undefined }
    | { type: MessageType.ACTION_TRIGGER_CONNECT; payload: undefined }
    | {
          type: MessageType.ACTION_SIGN_MESSAGE;
          payload: { message: string; messageId: string; ownerAddress?: Address };
      }
    | {
          type: MessageType.ACTION_PROVIDER_WIDGET;
          payload: {
              providerWidgetMode: "REDIRECT" | "POPUP";
              redirectUrl: string;
              url: string;
              workflowId: string;
          };
      }
    | {
          type: MessageType.ACTION_SIGN_TYPED_DATA;
          payload: { typedData: TypedData; messageId: string; ownerAddress?: Address; chainId?: number };
      }
    | {
          type: MessageType.ACTION_SIGN_AUTH_MESSAGE;
          payload: { messageId: string; message: string; address: Address; walletType: WalletAuthChain };
      }
    | {
          type: MessageType.ACTION_GENERATE_FEE_SPONSORSHIP_GRANT;
          payload: { messageId: string; grantPayload: string };
      }
    | {
          type: MessageType.EVENT_RESIZE;
          payload: { width?: number; height?: number };
      }
    | {
          type: MessageType.ACTION_GET_BALANCES;
          payload: { funderAddress: Address; messageId: string };
      }
    | {
          type: MessageType.ACTION_TRANSFER_FUNDS;
          payload: { funderAddress: Address; instruction: ITransferInInstruction; messageId: string };
      }
    | {
          type: MessageType.ACTION_GET_FEE;
          payload: { funderAddress: Address; assetIdentifier: string; amount: string; messageId: string };
      };

export type MessageResponse =
    | {
          type: MessageType.ACTION_TRANSACTION;
          payload:
              // A SOL "receipt" is just the signature — the widget never waits for confirmation.
              | { messageId: string; txReceipt: TransactionReceipt | SolTransactionReceipt; error: null }
              | { messageId: string; txReceipt: null; error: string };
      }
    | {
          type: MessageType.ACTION_SIGN_MESSAGE;
          payload:
              | { messageId: string; signature: string; error: null }
              | { messageId: string; signature: null; error: string };
      }
    | {
          type: MessageType.ACTION_SIGN_TYPED_DATA;
          payload:
              | { messageId: string; signature: string; error: null }
              | { messageId: string; signature: null; error: string };
      }
    | {
          type: MessageType.ACTION_SIGN_AUTH_MESSAGE;
          payload:
              | { messageId: string; signature: string; error: null }
              | { messageId: string; signature: null; error: string };
      }
    | {
          type: MessageType.ACTION_GENERATE_FEE_SPONSORSHIP_GRANT;
          payload:
              | { messageId: string; grant: FeeSponsorshipGrant; error: null }
              | { messageId: string; grant: null; error: string };
      }
    | {
          type: MessageType.ACTION_GET_BALANCES;
          payload:
              | { messageId: string; balances: [assetIdentifier: string, balance: string][]; error: null }
              | { messageId: string; balances: null; error: string };
      }
    | {
          type: MessageType.ACTION_TRANSFER_FUNDS;
          payload: { messageId: string; ok: true; error: null } | { messageId: string; ok: false; error: string };
      }
    | {
          type: MessageType.ACTION_GET_FEE;
          payload: { messageId: string; fee: string; error: null } | { messageId: string; fee: null; error: string };
      };

// ============================================
// Widget Load Error Types
// ============================================

export enum WidgetLoadFailureReason {
    /** Host page's Content-Security-Policy blocked the iframe src */
    CSP_BLOCKED = "CSP_BLOCKED",
    /** Network failure: DNS, firewall, proxy, or server unreachable */
    NETWORK_FAILURE = "NETWORK_FAILURE",
    /** The iframe element was removed from the DOM by external code */
    IFRAME_REMOVED = "IFRAME_REMOVED",
    /** A browser extension or privacy tool blocked the request */
    RESOURCE_BLOCKED = "RESOURCE_BLOCKED",
    /** Unable to determine the specific failure reason */
    UNKNOWN = "UNKNOWN",
}

export type WidgetLoadDiagnostics = {
    onloadFired: boolean;
    cspViolationDetected: boolean;
    cspBlockedURI: string | null;
    iframeInDOM: boolean;
    resourceTimingEntryExists: boolean;
    elapsedMs: number;
};

export class WidgetLoadError extends Error {
    override name = "WidgetLoadError" as const;
    readonly reason: WidgetLoadFailureReason;
    readonly diagnostics: WidgetLoadDiagnostics;

    constructor(message: string, reason: WidgetLoadFailureReason, diagnostics: WidgetLoadDiagnostics) {
        super(message);
        this.reason = reason;
        this.diagnostics = diagnostics;
        Object.setPrototypeOf(this, WidgetLoadError.prototype);
    }

    static isWidgetLoadError(error: unknown): error is WidgetLoadError {
        return error instanceof WidgetLoadError;
    }
}

// ============================================
// Public SDK Config Surface
// Nested, public config — normalized to the flat engine params above by `normalizeConfig`.
// ============================================

/** Deposit/onramp group. */
export interface DepositConfig {
    /** Source/funding assets (public successor of dangerouslyOverrideInputs). */
    inputs?: IAsset[];
    /** Assets received. Defaults to `[]` (no filter — all assets accepted) when unset. */
    outputs?: IAsset[];
    /** Who funds the deposit (no singular `funder`). */
    funders?: FunderRole[];
    destinationAddress?: Address;
    /** Which inbound funding tab the landing opens on. Seeds the store once; omit for today's 'crypto' default. */
    defaultTab?: 'crypto' | 'cash';
    /**
     * Integrator-defined strings attached to each deposit the widget creates, returned as `tags` on the
     * payment status and as `event.tags` in webhook events (e.g. `["sol:<userSolanaAddress>"]`). No empty
     * strings; at most 400 characters across all tags combined.
     */
    tags?: string[];
}

/** Withdrawal/offramp group. */
export interface WithdrawalConfig {
    /** Assets withdrawn. Defaults to deposit.outputs minus fiat at open time. Pass `[]` to accept all assets. */
    inputs?: IAsset[];
    /** FILTER on allowed targets — DEFERRED (no flat target today); dropped by normalizeConfig. */
    outputs?: IAsset[];
    /** Required to openWithdrawal (no fallback). */
    funder?: FunderRole;
    destinationAddress?: Address;
    /** Integrator-defined strings attached to each withdrawal the widget creates. Same rules as `deposit.tags`. */
    tags?: string[];
}

/**
 * Fee-sponsorship group. Both fields are required together (the signer is meaningless without the
 * profile name and vice-versa). The SDK signs a widget-supplied payload; it never builds it.
 */
export interface FeeSponsorshipConfig {
    /** Fee-sponsorship profile name. Live-pushed to the widget (serialized to query params as `profileName`). */
    profileName: string;
    /** Pure signer: receives the widget's canonical payload string, returns ONLY the HMAC signature. */
    generateGrant: (payload: string) => Promise<string>;
}

/** @internal Dangerous internal/testing overrides; replaced wholesale on update. Out of generated docs. */
export type DangerousOverrides = {
    dangerouslyOverrideApiBaseUrl?: string;
    dangerouslyOverrideHallidayDomainName?: string;
    dangerouslyOverrideIPAddress?: string;
    dangerouslyOverrideFeatureFlags?: FeatureFlags;
    dangerouslyOverrideOnramps?: RampName[];
    dangerouslyOverrideOfframps?: RampName[];
    dangerouslyOverrideFlags?: Flag[];
    dangerouslyOverrideHops?: string[];
    dangerouslyOverrideDemoScenario?: string;
};

/** Public, nested SDK configuration accepted by `HallidayPayments`. */
export interface HallidayPaymentsConfig {
    apiKey: string;
    sandbox?: boolean;
    owner?: Owner;
    /** Request handler invoked when the widget asks the host to connect a wallet (renamed from onConnectUserWallet). */
    onConnectWallet?: () => void;
    /** Fee-sponsorship group; both fields required together. Signer signs a widget-supplied payload. */
    feeSponsorship?: FeeSponsorshipConfig;
    deposit?: DepositConfig;
    withdrawal?: WithdrawalConfig;
    customStyles?: CustomStyles;
    fontName?: string;
    headerTitle?: string;
    /** present ⇒ EMBED; absent ⇒ MODAL */
    targetElementId?: string;
    /** @internal */ autoPreload?: boolean;
    /** @internal */ instanceId?: string;
    /** @internal */ dangerousOverrides?: DangerousOverrides;
}

// ============================================
// Reactive surface (HallidaySnapshot is public; the store mechanism is @internal)
// ============================================

export type HallidayEvent = "status" | "error" | "close";
export type HallidayErrorSource = "preload" | "openDeposit" | "openWithdrawal" | "resolution" | "load";
export type HallidayRuntimeError = (WidgetLoadError | Error) & { source: HallidayErrorSource };
export type HallidayEventHandler<E extends HallidayEvent> = E extends "status"
    ? (s: { type: string; payload: OrderStatus }) => void
    : E extends "error"
      ? (e: HallidayRuntimeError) => void
      : () => void; // "close"
export interface HallidaySnapshot {
    isReady: boolean;
    isOpen: boolean;
    status: { type: string; payload: OrderStatus } | null;
    error: HallidayRuntimeError | null;
    notifications: OrderNotification[]; // full current set of orders needing attention; initial []
}
